import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import "./select-input.scss";

export interface SelectInputOption {
  label: string;
  value: string;
}

export interface SelectInputProps {
  label?: string;
  options: SelectInputOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string, option: SelectInputOption) => void;
  disabled?: boolean;
  focusBorderWidth?: string;
  className?: string;
  useCustomStyle?: boolean;
  inputClassName?: string;
}

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 0.2s ease" }}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TickIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M16.6667 5L7.5 14.1667L3.33334 10"
      stroke="#1F1F1F"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SelectInput: React.FC<SelectInputProps> = ({
  label,
  options,
  value: controlledValue,
  defaultValue,
  placeholder = "Select...",
  onChange,
  disabled = false,
  focusBorderWidth,
  className = "",
  useCustomStyle = false,
  inputClassName = "",
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const activeValue = isControlled ? controlledValue : internalValue;

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">("bottom");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === activeValue);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    const option = options.find((o) => o.value === newValue);
    if (!option) return;

    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue, option);
  };

  const handleOptionSelect = (option: SelectInputOption) => {
    if (!isControlled) {
      setInternalValue(option.value);
    }
    onChange?.(option.value, option);
    setIsOpen(false);
  };

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < window.innerHeight * 0.45;

    setDropdownPosition(openAbove ? "top" : "bottom");
    setPortalStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top }
        : { top: rect.bottom }),
    });
  }, []);

  const toggleOpen = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
    } else {
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (!isOpen || !useCustomStyle) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => updateDropdownPosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen, useCustomStyle, updateDropdownPosition]);

  if (useCustomStyle) {
    return (
      <div
        className={`select-input ${className}`}
        style={focusBorderWidth ? { "--select-input-focus-border-width": focusBorderWidth } as React.CSSProperties : undefined}
      >
        {label && (
          <label className="select-input__label">{label}</label>
        )}

        <div
          className={`select-input__trigger ${isOpen ? "select-input__trigger--open" : ""} ${disabled ? "select-input__trigger--disabled" : ""} ${inputClassName}`}
          onClick={toggleOpen}
          ref={triggerRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={label}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleOpen();
            } else if (e.key === "Escape") {
              setIsOpen(false);
            }
          }}
        >
          {selectedOption ? (
            <span className="select-input__selected-value">{selectedOption.label}</span>
          ) : (
            <span className="select-input__placeholder">{placeholder}</span>
          )}
          <span className="select-input__icon" aria-hidden="true">
            <ChevronIcon open={isOpen} />
          </span>
        </div>

        {isOpen &&
          ReactDOM.createPortal(
            <div
              className={`select-input__dropdown select-input__dropdown--${dropdownPosition}`}
              style={portalStyle}
              ref={dropdownRef}
              role="listbox"
            >
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`select-input__option ${activeValue === option.value ? "select-input__option--selected" : ""}`}
                  onClick={() => handleOptionSelect(option)}
                  role="option"
                  aria-selected={activeValue === option.value}
                >
                  {option.label}
                  {activeValue === option.value && <TickIcon />}
                </div>
              ))}
            </div>,
            document.body
          )}
      </div>
    );
  }

  return (
    <div
      className={`select-input ${className}`}
      style={focusBorderWidth ? { "--select-input-focus-border-width": focusBorderWidth } as React.CSSProperties : undefined}
    >
      {label && (
        <label className="select-input__label">{label}</label>
      )}

      <div className="select-input__wrapper">
        <select
          className={`select-input__native ${inputClassName}`}
          value={activeValue}
          onChange={handleChange}
          disabled={disabled}
          aria-label={label}
        >
          {!activeValue && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <span className="select-input__icon" aria-hidden="true">
          <ChevronIcon open={false} />
        </span>
      </div>
    </div>
  );
};
